import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { FileUploader } from "@/lib/FileUpload/FileUploader";
import { IMAGE_AND_PDF_ACCEPT } from "@/lib/FileUpload/accept";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  analyzeReceiptImageMutation,
  createReceiptMutation,
} from "@/app/Refunds/GraphQL/refundMutations";
import { useState } from "react";
import { Currency, ModelType, ReceiptType } from "@/gql/graphql";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { ReceiptTypeInput } from "@/app/Refunds/Components/ReceiptTypeInput";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { Loader2Icon } from "lucide-react";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";

const receiptFormSchema = z.object({
  type: z.nativeEnum(ReceiptType),
  amount_inc_tax: z.number().min(1, "Amount is required"),
  amount_tax: z.number().min(0, "Tax amount is required"),
  currency: z.nativeEnum(Currency),
  date: z
    .string()
    .min(1, "Date is required")
    .nullable()
    .refine((val) => val !== null && val.length > 0, "Date is required"),
});

type AnalyzedReceipt = {
  amount_inc_tax: number | null;
  amount_tax: number | null;
  currency: Currency | null;
  date: string | null;
  description: string | null;
  filePath: string;
  imageUrl: string;
};

type ReceiptFormValues = z.infer<typeof receiptFormSchema>;

export function UploadReceiptDialog({
  objectId,
  objectType,
}: {
  objectId: string;
  objectType: ModelType;
}) {
  const { close } = useDialogState();
  const [step, setStep] = useState<"upload" | "analyzing" | "confirm">(
    "upload",
  );
  const [analyzedReceipt, setAnalyzedReceipt] =
    useState<AnalyzedReceipt | null>(null);

  const { mutateAsync: analyzeReceipt } = useGqlMutation(
    analyzeReceiptImageMutation,
  );
  const { mutateAsync: createReceipt } = useGqlMutation(createReceiptMutation);

  const handleUpload = async (uploads: { file: File; filePath: string }[]) => {
    if (uploads.length === 0) return;

    const upload = uploads[0];
    setStep("analyzing");

    try {
      const result = await analyzeReceipt({ filePath: upload.filePath });
      setAnalyzedReceipt({
        amount_inc_tax: result.analyzeReceiptImage.amount_inc_tax ?? null,
        amount_tax: result.analyzeReceiptImage.amount_tax ?? null,
        currency: (result.analyzeReceiptImage.currency as Currency) ?? null,
        date: result.analyzeReceiptImage.date ?? null,
        description: result.analyzeReceiptImage.description ?? null,
        filePath: result.analyzeReceiptImage.filePath,
        imageUrl: result.analyzeReceiptImage.imageUrl,
      });
      setStep("confirm");
    } catch (error) {
      console.error("Failed to analyze receipt:", error);
      setStep("upload");
    }
  };

  const handleConfirm = async (values: ReceiptFormValues) => {
    if (!analyzedReceipt || !values.date) return;

    await createReceipt({
      input: {
        objectId,
        objectType,
        filePath: analyzedReceipt.filePath,
        type: values.type,
        amount_inc_tax: values.amount_inc_tax,
        amount_tax: values.amount_tax,
        currency: values.currency,
        date: values.date,
        description: analyzedReceipt.description,
      },
    });

    return close();
  };

  return (
    <BaseDialog title="Upload Receipt" size="xl">
      {step === "upload" ? (
        <FileUploader
          onUpload={handleUpload}
          maxFileCount={1}
          accept={IMAGE_AND_PDF_ACCEPT}
          fileTypeLabel="Supports JPEG, PNG, WebP, AVIF, and PDF formats"
        />
      ) : step === "analyzing" ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2Icon className="text-primary mb-4 size-12 animate-spin" />
          <p className="text-lg font-medium">Analyzing receipt...</p>
          <p className="text-muted-foreground text-sm">
            Extracting amount, date, and currency
          </p>
        </div>
      ) : analyzedReceipt ? (
        <ReceiptConfirmForm
          analyzedReceipt={analyzedReceipt}
          onConfirm={handleConfirm}
        />
      ) : null}
    </BaseDialog>
  );
}

function ReceiptConfirmForm({
  analyzedReceipt,
  onConfirm,
}: {
  analyzedReceipt: AnalyzedReceipt;
  onConfirm: (values: ReceiptFormValues) => Promise<void>;
}) {
  return (
    <div className="grid grid-cols-2 gap-6">
      <div>
        <img
          src={analyzedReceipt.imageUrl}
          alt="Receipt"
          className="h-auto max-h-80 w-full rounded-lg border object-contain"
        />
      </div>

      <GenericForm<ReceiptFormValues>
        initialValues={{
          type: ReceiptType.Fuel,
          amount_inc_tax: analyzedReceipt.amount_inc_tax ?? 0,
          amount_tax: analyzedReceipt.amount_tax ?? 0,
          currency: analyzedReceipt.currency ?? Currency.Aud,
          date: analyzedReceipt.date ?? null,
        }}
        onSubmit={onConfirm}
        validationSchema={toFormikValidationSchema(receiptFormSchema)}
        className="flex flex-col"
      >
        <ReceiptFormFields />
        <DialogSubmitButton text="Add Receipt" />
      </GenericForm>
    </div>
  );
}

function ReceiptFormFields() {
  const { values } = useFormState<ReceiptFormValues>();

  return (
    <>
      <div className="space-y-4">
        <ReceiptTypeInput name="type" label="Type" />
        <CurrencyTypeInput name="currency" label="Currency" />
        <div>
          <CurrencyInput
            name="amount_inc_tax"
            label="Amount (inc. tax)"
            currency={values.currency ?? Currency.Aud}
          />
          <div className="mt-1">
            <CurrencyInput
              name="amount_tax"
              label="Tax"
              currency={values.currency ?? Currency.Aud}
              className="text-sm text-zinc-500"
            />
          </div>
        </div>
        <DateInput name="date" label="Receipt Date" />
      </div>
    </>
  );
}
